import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useMarketingAudio from '../hooks/useMarketingAudio';
import config from '../config';
import './MarketingVideo.css';

const SLIDE_DURATIONS = [3500, 4000, 4500, 4500, 4000, 3500, null];

// ── Slide 0 — Magic Hook ──────────────────────────────────────
const Slide0 = () => (
  <div className="mv-slide-inner slide-dark">
    <div className="mv-sparkles">
      {[...Array(12)].map((_, i) => (
        <span key={i} className={`mv-sparkle sp-${i}`}>✨</span>
      ))}
    </div>
    <div className="mv-slide-content center">
      <div className="mv-emoji anim-1">🌟</div>
      <p className="mv-app-name anim-2">{config.APP_NAME}</p>
      <h1 className="mv-headline anim-3">What if learning felt like magic?</h1>
      <p className="mv-sub anim-4">A world built just for your special child</p>
      <div className="mv-tagline anim-5">
        <span className="mv-badge">💙 Designed for learning differences</span>
        <span className="mv-badge">🎮 Learning through play</span>
        <span className="mv-badge">🏆 Progress you can see</span>
      </div>
    </div>
  </div>
);

// ── Slide 1 — Problem / Promise ───────────────────────────────
const Slide1 = () => (
  <div className="mv-slide-inner slide-teal">
    <div className="mv-slide-content center">
      <div className="mv-emoji anim-1">💙</div>
      <h1 className="mv-headline anim-2">Every child deserves to shine.</h1>
      <p className="mv-body anim-3">
        Learning differences don't mean learning less.<br />
        They mean <strong>learning differently.</strong>
      </p>
      <div className="mv-divider anim-4" />
      <p className="mv-body anim-5">
        Most apps weren't built for your child.<br />
        <strong className="mv-highlight">We built this one just for them.</strong>
      </p>
    </div>
  </div>
);

// ── Slide 2 — Sight Words Demo ────────────────────────────────
const Slide2 = () => (
  <div className="mv-slide-inner slide-dark">
    <div className="mv-slide-content split">
      <div className="mv-copy">
        <div className="mv-activity-label anim-1">👁️ SIGHT WORDS</div>
        <h2 className="mv-feature-title anim-2">They say it.<br />They master it.</h2>
        <p className="mv-feature-desc anim-3">
          Voice recognition listens as your child reads each word out loud.
          Every correct answer earns a star.
        </p>
      </div>
      <div className="mv-phone-mockup anim-phone">
        <div className="mv-phone-frame">
          <div className="mv-phone-notch" />
          <div className="mv-phone-screen">
            <div className="mv-word-card">
              <div className="mv-word-label">Read this word:</div>
              <div className="mv-big-word">BUTTERFLY</div>
              <div className="mv-mic-btn">
                <span className="mv-mic-icon">🎤</span>
              </div>
              <div className="mv-stars-row">
                <span className="mv-star filled anim-star-1">⭐</span>
                <span className="mv-star filled anim-star-2">⭐</span>
                <span className="mv-star anim-star-3">☆</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── Slide 3 — Scramble Words Demo ─────────────────────────────
const Slide3 = () => (
  <div className="mv-slide-inner slide-coral">
    <div className="mv-slide-content split reverse">
      <div className="mv-phone-mockup anim-phone">
        <div className="mv-phone-frame dark">
          <div className="mv-phone-notch" />
          <div className="mv-phone-screen">
            <div className="mv-scramble-demo">
              <div className="mv-scramble-label">Unscramble it!</div>
              <div className="mv-letter-boxes">
                {['P','L','A','N','T'].map((l, i) => (
                  <div key={i} className={`mv-letter-box anim-letter-${i}`}>{l}</div>
                ))}
              </div>
              <div className="mv-scramble-hint">Was: N·T·A·L·P → You got it! ✓</div>
              <div className="mv-score-badge">+10 pts! 🎉</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mv-copy">
        <div className="mv-activity-label anim-1">🔀 WORD SCRAMBLE</div>
        <h2 className="mv-feature-title anim-2">Unlock the puzzle.<br />Build confidence.</h2>
        <p className="mv-feature-desc anim-3">
          Rearrange jumbled letters into real words.
          Each win builds spelling skills and cognitive strength.
        </p>
      </div>
    </div>
  </div>
);

// ── Slide 4 — Category Words Demo ────────────────────────────
const Slide4 = () => (
  <div className="mv-slide-inner slide-teal">
    <div className="mv-slide-content split">
      <div className="mv-copy">
        <div className="mv-activity-label anim-1">🏷️ CATEGORY MATCH</div>
        <h2 className="mv-feature-title anim-2">Sort. Learn.<br />Understand.</h2>
        <p className="mv-feature-desc anim-3">
          Match words to their category. Builds vocabulary and
          conceptual understanding across real-world themes.
        </p>
        <div className="mv-categories-grid anim-4">
          {['🐾 Animals','🍎 Food','🌈 Colors','⛅ Weather','⚽ Sports','👕 Clothing'].map((c, i) => (
            <span key={i} className="mv-cat-chip">{c}</span>
          ))}
        </div>
      </div>
      <div className="mv-phone-mockup anim-phone">
        <div className="mv-phone-frame">
          <div className="mv-phone-notch" />
          <div className="mv-phone-screen">
            <div className="mv-category-demo">
              <div className="mv-category-label">Where does this belong?</div>
              <div className="mv-category-word">APPLE</div>
              <div className="mv-category-options">
                <div className="mv-cat-option correct">🍎 FOOD ✓</div>
                <div className="mv-cat-option">🐾 ANIMALS</div>
                <div className="mv-cat-option">🌈 COLORS</div>
                <div className="mv-cat-option">🚗 TRANSPORT</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── Slide 5 — Progress & Safety ───────────────────────────────
const Slide5 = () => (
  <div className="mv-slide-inner slide-dark">
    <div className="mv-slide-content center">
      <h2 className="mv-headline anim-1">Watch them grow. Every day.</h2>
      <div className="mv-badges-row anim-2">
        {['🌟 First Word','🔥 On a Roll','📚 Book Worm','🏆 Champion','💪 Word Hero'].map((b, i) => (
          <div key={i} className={`mv-achievement-badge badge-${i}`}>{b}</div>
        ))}
      </div>
      <div className="mv-safety-grid anim-3">
        <div className="mv-safety-item">
          <span className="mv-safety-icon">🛡️</span>
          <span>No ads. No distractions.</span>
        </div>
        <div className="mv-safety-item">
          <span className="mv-safety-icon">🔒</span>
          <span>Admin-controlled content</span>
        </div>
        <div className="mv-safety-item">
          <span className="mv-safety-icon">📊</span>
          <span>Progress visible to parents</span>
        </div>
        <div className="mv-safety-item">
          <span className="mv-safety-icon">🎯</span>
          <span>Personalized word tiers</span>
        </div>
      </div>
    </div>
  </div>
);

// ── Slide 6 — CTA ─────────────────────────────────────────────
const Slide6 = ({ navigate }) => (
  <div className="mv-slide-inner slide-cta">
    <div className="mv-cta-bg-shapes">
      {[...Array(6)].map((_, i) => (
        <div key={i} className={`mv-cta-shape shape-${i}`} />
      ))}
    </div>
    <div className="mv-slide-content center">
      <div className="mv-emoji anim-1">🚀</div>
      <h1 className="mv-cta-headline anim-2">
        Ready to start their adventure?
      </h1>
      <p className="mv-cta-sub anim-3">
        Give your child the learning experience they deserve.<br />
        <strong>Fun. Safe. Effective.</strong>
      </p>
      <div className="mv-cta-buttons anim-4">
        <button className="mv-cta-primary" onClick={() => navigate('/login')}>
          Start {config.APP_NAME} 🎉
        </button>
        <button className="mv-cta-secondary" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
      </div>
      <p className="mv-cta-note anim-5">
        {config.APP_NAME} — designed with love for children with intellectual disabilities and learning differences.
      </p>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────
const LAST_SLIDE = SLIDE_DURATIONS.length - 1;

const MarketingVideo = () => {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);
  const [muted, setMuted] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  const { initAndPlay, stopAudio, syncMute } = useMarketingAudio();

  const duration = SLIDE_DURATIONS[slide];

  // Try auto-start on mount (works if user arrived via a click)
  useEffect(() => { initAndPlay(); }, [initAndPlay]);

  // Sync mute with audio
  useEffect(() => { syncMute(muted); }, [muted, syncMute]);

  // Stop music when the last (CTA) slide is reached
  useEffect(() => {
    if (slide === LAST_SLIDE) stopAudio();
  }, [slide, stopAudio]);

  // Auto-advance slides
  useEffect(() => {
    if (!duration) return;
    const t = setTimeout(() => {
      setSlide(prev => Math.min(prev + 1, LAST_SLIDE));
      setAnimKey(k => k + 1);
    }, duration);
    return () => clearTimeout(t);
  }, [slide, duration]);

  const goToSlide = (i) => {
    setSlide(i);
    setAnimKey(k => k + 1);
    initAndPlay();
  };

  const slides = [
    <Slide0 />, <Slide1 />, <Slide2 />, <Slide3 />,
    <Slide4 />, <Slide5 />, <Slide6 navigate={navigate} />
  ];

  return (
    <div className="mv-page" onClick={initAndPlay}>
      <div className="mv-topbar">
        <button className="mv-home-btn" onClick={() => navigate('/')}>
          ← Home
        </button>

        <div className="mv-dot-nav">
          {SLIDE_DURATIONS.map((_, i) => (
            <button
              key={i}
              className={`mv-dot ${i === slide ? 'active' : i < slide ? 'done' : ''}`}
              onClick={() => goToSlide(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        <button
          className="mv-mute-btn mv-mute-btn--topbar"
          onClick={() => setMuted(m => !m)}
          aria-label={muted ? 'Unmute music' : 'Mute music'}
          title={muted ? 'Unmute music' : 'Mute music'}
        >
          {muted ? '🔇' : '🎵'}
        </button>
      </div>

      {duration && (
        <div className="mv-progress-bar">
          <div
            key={animKey}
            className="mv-progress-fill"
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      )}

      <div className="mv-slides-container">
        {slides.map((content, i) => (
          <div
            key={i}
            className={`mv-slide ${i === slide ? 'active' : ''}`}
          >
            {content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarketingVideo;
